// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Hatch — one round of a live race, decoupled from Trystero, the DOM and the clock (driven
// by paint()/tick()), which is what makes the host-transfer contract testable without a
// network (tests/takeover.test.ts).
//
// PARALLEL SAME-SEED RACE: every peer builds its OWN board(s) — a pure function of the frozen
// seed — and reaches its OWN game-over with no host help. NO game state ever crosses the wire
// — only a tiny status gossip (score + mistakes as progress, and on finishing whether the
// picture was revealed) so the results can show everyone's run. HOST TRANSFER IS A PURE
// DISPLAY CONCERN; onHostChange is wired anyway.

import { createGame, drainEvents, paint as paintGame, tick as tickGame, type GameEvent, type GameState, type PaintAction } from './game';
import type { Mode } from './modes';

export interface PeerResult {
  seat: number;
  name: string;
  score: number;
  mistakes: number;
  solved: boolean; // revealed the picture (Zen/Strikes) / banked at least one (Blitz)
  finished: boolean;
  left: boolean;
}

/** Tiny wire status. Progress rides `s`/`m`; the finish rides `d`/`w`. */
export interface RaceMsg {
  s?: number; // score
  m?: number; // mistakes (progress)
  d?: boolean; // done
  w?: boolean; // solved / won
}

export interface RaceDeps {
  send: (m: RaceMsg) => void;
  onChange: () => void;
  onSelfEnd: (g: GameState) => void;
}

export interface RaceOpts {
  mode: Mode;
  seed: number;
  selfSeat: number | null;
  names: string[];
  isHost: boolean;
  deps: RaceDeps;
}

export function seatOf(roster: ReadonlyArray<{ id: string }>, selfId: string): number | null {
  const i = roster.findIndex((p) => p.id === selfId);
  return i < 0 ? null : i;
}

/** A debounced one-shot: poke() ensures fn fires once, `ms` after the FIRST poke of a window;
 *  further pokes while pending do not push it back. */
export function createOneShot(ms: number, fn: () => void): { poke: () => void; cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return {
    poke() {
      if (timer !== null) return;
      timer = setTimeout(() => {
        timer = null;
        fn();
      }, ms);
    },
    cancel() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}

export class Race {
  state: GameState;
  readonly selfSeat: number | null;
  readonly mode: Mode;
  private host: boolean;
  private readonly deps: RaceDeps;
  private ended = false;
  readonly results = new Map<number, PeerResult>();

  constructor(o: RaceOpts) {
    this.mode = o.mode;
    this.state = createGame(o.mode, o.seed);
    this.selfSeat = o.selfSeat;
    this.host = o.isHost;
    this.deps = o.deps;
    o.names.forEach((name, seat) => {
      this.results.set(seat, { seat, name, score: 0, mistakes: 0, solved: false, finished: false, left: false });
    });
  }

  get isHost(): boolean {
    return this.host;
  }
  setHost(v: boolean): void {
    this.host = v;
  }

  private self(): PeerResult | null {
    return this.selfSeat === null ? null : (this.results.get(this.selfSeat) ?? null);
  }

  private syncSelf(): void {
    const me = this.self();
    if (!me) return;
    me.score = this.state.score;
    me.mistakes = this.state.mistakes;
  }

  /** Advance the local clock by `dtMs`. Spectators no-op. Returns queued events. */
  tick(dtMs: number): GameEvent[] {
    if (this.selfSeat === null || this.state.over) return [];
    tickGame(this.state, dtMs);
    const events = drainEvents(this.state);
    if (this.state.over && !this.ended) this.endSelf();
    return events;
  }

  /** Apply a paint action to a cell. Returns queued events. */
  paint(idx: number, action: PaintAction): GameEvent[] {
    if (this.selfSeat === null || this.state.over) return [];
    const scoreBefore = this.state.score;
    const mistakesBefore = this.state.mistakes;
    paintGame(this.state, idx, action);
    const events = drainEvents(this.state);
    this.syncSelf();
    if (this.state.score !== scoreBefore || this.state.mistakes !== mistakesBefore) {
      this.deps.send({ s: this.state.score, m: this.state.mistakes });
      this.deps.onChange();
    }
    if (this.state.over && !this.ended) this.endSelf();
    return events;
  }

  private endSelf(): void {
    if (this.ended) return;
    this.ended = true;
    const me = this.self();
    if (me) {
      me.finished = true;
      me.score = this.state.score;
      me.mistakes = this.state.mistakes;
      me.solved = this.state.won;
    }
    this.deps.send({ d: true, s: this.state.score, m: this.state.mistakes, w: this.state.won });
    this.deps.onChange();
    this.deps.onSelfEnd(this.state);
  }

  /** A status update from another peer. Validated, never trusted. */
  receive(msg: RaceMsg, from: number): void {
    if (from === this.selfSeat) return; // never let the wire overwrite our own run
    const r = this.results.get(from);
    if (!r || r.left) return;
    if (typeof msg.s === 'number' && Number.isFinite(msg.s)) r.score = Math.max(0, msg.s | 0);
    if (typeof msg.m === 'number' && Number.isFinite(msg.m)) r.mistakes = Math.max(0, msg.m | 0);
    if (msg.d) r.finished = true;
    if (typeof msg.w === 'boolean') r.solved = msg.w;
    this.deps.onChange();
  }

  peerLeft(seat: number): void {
    const r = this.results.get(seat);
    if (r && !r.finished) {
      r.left = true;
      this.deps.onChange();
    }
  }

  everyoneDone(): boolean {
    for (const r of this.results.values()) if (!r.finished && !r.left) return false;
    return true;
  }

  /** Highest score first; unfinished by score; departed peers last. */
  ranked(): PeerResult[] {
    return [...this.results.values()].sort((a, b) => {
      const rank = (r: PeerResult): number => (r.left ? 2 : r.finished ? 0 : 1);
      const ra = rank(a);
      const rb = rank(b);
      if (ra !== rb) return ra - rb;
      if (b.score !== a.score) return b.score - a.score;
      return a.seat - b.seat;
    });
  }
}
